import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bot, User, Sparkles, Clock, Target, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface CampaignChoicePageProps {
  campaignName?: string;
  onManualChoice: () => void;
  onAIChoice: () => void;
}

export const CampaignChoicePage: React.FC<CampaignChoicePageProps> = ({
  campaignName,
  onManualChoice,
  onAIChoice
}) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleManualChoice = () => {
    onManualChoice();
  };

  const handleAIChoice = () => {
    // Navigate to AI chat page instead of showing inline
    navigate('/campaign/ai-chat', { state: { campaignName } });
    onAIChoice();
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-primary/5 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <Target className="h-4 w-4" />
              <span className="text-sm font-medium">
                {language === 'ar' ? 'إنشاء حملة إعلانية' : 'Campaign Creation'}
              </span>
            </div>
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent py-1">
              {language === 'ar' ? 'كيف تريد إنشاء حملتك؟' : 'How would you like to create your campaign?'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'ar' 
                ? 'اختر الطريقة المناسبة لك لإنشاء حملة إعلانية احترافية'
                : 'Choose the method that suits you best for creating a professional campaign'}
            </p>
            {campaignName && (
              <Badge variant="secondary" className="mt-4 text-base px-4 py-2">
                {language === 'ar' ? `الحملة: ${campaignName}` : `Campaign: ${campaignName}`}
              </Badge>
            )}
          </motion.div>

          {/* Choice Cards */}
          <motion.div 
            variants={itemVariants}
            className="grid md:grid-cols-2 gap-6"
          >
            {/* AI Assistant Option */}
            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 cursor-pointer group h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CardHeader className="relative z-10 pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-primary/10 rounded-xl">
                      <Bot className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">
                        {language === 'ar' ? 'مساعد الذكي الاصطناعي' : 'AI Assistant'}
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        {language === 'ar' ? 'مُوصى به' : 'Recommended'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative z-10 space-y-4">
                  <p className="text-muted-foreground">
                    {language === 'ar' 
                      ? 'دع الذكي الاصطناعي يساعدك في إنشاء حملة مثالية بناءً على أهدافك وميزانيتك'
                      : 'Let AI help you create the perfect campaign based on your goals and budget'}
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{language === 'ar' ? 'تحليل ذكي للجمهور المستهدف' : 'Smart audience analysis'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{language === 'ar' ? 'اختيار المواقع الأمثل' : 'Optimal location selection'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{language === 'ar' ? 'تحسين الميزانية والتوقيت' : 'Budget & timing optimization'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-primary">
                    <Clock className="h-4 w-4" />
                    <span>{language === 'ar' ? '~3 دقائق' : '~3 minutes'}</span>
                  </div>

                  <Button 
                    onClick={handleAIChoice}
                    className="w-full group/btn bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {language === 'ar' ? 'ابدأ مع الذكي الاصطناعي' : 'Start with AI'}
                    <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Manual Option */}
            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="relative overflow-hidden border-2 hover:border-accent/50 transition-all duration-300 cursor-pointer group h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-muted/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CardHeader className="relative z-10 pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-accent/10 rounded-xl">
                      <User className="h-6 w-6 text-accent-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">
                        {language === 'ar' ? 'إنشاء يدوي' : 'Manual Creation'}
                      </CardTitle>
                      <Badge variant="outline" className="text-xs">
                        {language === 'ar' ? 'تحكم كامل' : 'Full Control'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative z-10 space-y-4">
                  <p className="text-muted-foreground">
                    {language === 'ar' 
                      ? 'قم بإنشاء حملتك بنفسك خطوة بخطوة مع التحكم الكامل في جميع التفاصيل'
                      : 'Create your campaign step by step with full control over every detail'}
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-500" />
                      <span>{language === 'ar' ? 'تخصيص كامل للحملة' : 'Complete customization'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-500" />
                      <span>{language === 'ar' ? 'اختيار يدوي للمواقع' : 'Manual location selection'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-500" />
                      <span>{language === 'ar' ? 'مرونة في الإعدادات' : 'Flexible settings'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-accent-foreground">
                    <Zap className="h-4 w-4" />
                    <span>{language === 'ar' ? '~5-10 دقائق' : '~5-10 minutes'}</span>
                  </div>

                  <Button 
                    onClick={handleManualChoice}
                    variant="outline"
                    className="w-full group/btn hover:bg-accent hover:text-accent-foreground"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {language === 'ar' ? 'إنشاء يدوي' : 'Create Manually'}
                    <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>

          {/* Features Comparison */}
          <motion.div variants={itemVariants} className="text-center">
            <p className="text-sm text-muted-foreground">
              {language === 'ar' 
                ? 'يمكنك تغيير الطريقة أو الاستعانة بالذكي الاصطناعي في أي وقت أثناء إنشاء الحملة'
                : 'You can always switch methods or get AI assistance during campaign creation'}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};